import { ComponentProps } from 'react';

import { CAR_PURCHASE_STATUS, CarGalleryNavigation } from '@/constants/constants';
import { CheckoutApiResponse } from '@/data/CheckoutApi/CheckoutApi.types';
import { CarFeatures } from '@/pdp/components/CarFeatures/CarFeatures';
import { CarKeyInformation } from '@/pdp/components/CarKeyInformation/CarKeyInformation';
import { KEY_ITEMS } from '@/pdp/components/CarKeyInformation/CarKeyInformation.type';
import { HIGHLIGHTED_FEATURES } from '@/pdp/components/HighlightedFeatureCarousel/HighlightedFeatureCarousel.type';
import { GalleryModalState } from '@/pdp/sections/Pdp/Pdp';
import { OrdersResponse } from '@/types/CataloguePage.types';
import { ProductDetailsPayload } from '@/types/ProductDetail.type';

export type CarDisplayState = 'loading' | 'available' | 'not-available' | 'continue-purchase' | 'my-purchase';

export const enum CAR_FEATURES {
  APPLE_CARPLAY_ANDROID_AUTO = '854220002',
  APPLE_CARPLAY = '854220014',
  ANDROID_AUTO = '854220013',
  WIRELESS_ANDROID_AUTO = '854220019',
  WIRELESS_APPLE_CARPLAY = '854220020',
  ADVANCED_CRUISE_CONTROL = '854220000',
  ALLOY_WHEELS = '854220001',
  GPS_NAVIGATION = '854220003',
  HEADS_UP_DISPLAY = '854220004',
  SUN_ROOF = '854220011',
  HEATED_VENTILATED_SEATS = '854220005',
  HEATED_SEATS = '854220015',
  VENTILATED_SEATS = '854220018',
  PUSH_START_KEYLESS_ENTRY = '854220007',
  PUSH_START = '854220016',
  KEYLESS_ENTRY = '854220021',
  LEATHER_SEATS = '854220006',
  REAR_PARKING_CAMERA = '854220008',
  REAR_PARKING_SENSOR = '854220009',
  ROOF_RACKS_RAILS = '854220010',
  ROOF_RACKS = '854220022',
  ROOF_RAILS = '854220017',
  TOW_PACK_BALL = '854220012',
  TOW_PACK = '854220023',
  BLIND_SPOT_MONITORING = '854220024',
  SURROUND_VIEW_CAMERA = '854220025',
}

export const formatCarKeyInformation = (
  productDetails: ProductDetailsPayload,
): ComponentProps<typeof CarKeyInformation> => ({
  items: [
    {
      key: KEY_ITEMS.KILOMETERS,
      value: productDetails.kilometres,
    },
    {
      key: KEY_ITEMS.COMPLIANCE_YEAR,
      value: `${productDetails.complianceYear}`,
    },
    {
      key: KEY_ITEMS.BUILD_YEAR,
      value: `${productDetails.buildYear}`,
    },
    {
      key: KEY_ITEMS.ENGINE_SIZE,
      value: productDetails.engineSize,
    },
    {
      key: KEY_ITEMS.FUEL_TYPE,
      value: productDetails.fuelType,
    },
    {
      key: KEY_ITEMS.TRANSMISSION,
      value: productDetails.transmission,
    },
    {
      key: KEY_ITEMS.SEATS,
      value: `${productDetails.seats}`,
    },
    {
      key: KEY_ITEMS.LAST_SERVICE_DATE,
      value: productDetails.lastService,
    },
    {
      key: KEY_ITEMS.VIN_NUMBER,
      value: productDetails.vin,
    },
  ],
});

// plain-string entries have no CT code to resolve, so only the legacy { key, label } shape counts
const highlightedFeatureCodes = (productDetails: ProductDetailsPayload): CAR_FEATURES[] =>
  productDetails.highlightedFeatures
    .filter((feature): feature is { key: string; label: string } => typeof feature !== 'string')
    .map((feature) => feature.key as CAR_FEATURES);

export const getHighlightedFeatureLabel = (feature: ProductDetailsPayload['highlightedFeatures'][number]): string =>
  typeof feature === 'string' ? feature : feature.label;

export const formatFeatures = (productDetails: ProductDetailsPayload) => ({
  features: highlightedFeatureCodes(productDetails),
  detailedHighlightedFeatures: (productDetails.detailedHighlightedFeatures ?? []) as HIGHLIGHTED_FEATURES[],
  carFeatures: {
    optionalFeatures: productDetails.optionalFeatures ?? [],
    standardFeatures: productDetails.standardFeatures ?? [],
    aftermarketFeatures: productDetails.aftermarketFeatures ?? [],
  },
});

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

export const formatFeaturesWithSorted = (productDetails: ProductDetailsPayload) => ({
  features: highlightedFeatureCodes(productDetails),
  carFeatures: {
    optionalFeatures: [...productDetails.optionalFeatures].sort(collator.compare) ?? [],
    standardFeatures: [...productDetails.standardFeatures].sort(collator.compare) ?? [],
    aftermarketFeatures: [...(productDetails.aftermarketFeatures || [])].sort(collator.compare) ?? [],
  },
});

export const formatCarFeatures = (productDetails: ProductDetailsPayload): ComponentProps<typeof CarFeatures> => ({
  standardFeatures: [...productDetails.standardFeatures],
  optionalFeatures: [...productDetails.optionalFeatures],
  aftermarketFeatures: [...(productDetails.aftermarketFeatures || [])],
});

export const getGalleryDisplayImageIndex = (productDetails: ProductDetailsPayload): number =>
  productDetails.images.gallery.findIndex((image) => image.background === true);

export const formatAncapRating = (rating: string) => (rating === '9 stars' ? 'Not rated' : rating);

export const formatCarDisplayState = (
  sku: string,
  apiCartData: CheckoutApiResponse | null,
  userOrders: OrdersResponse[],
  carStatus: CAR_PURCHASE_STATUS,
): CarDisplayState => {
  // Check if the product is in the user orders
  if (userOrders?.findIndex((item) => item.vehicle.sku === sku) >= 0) {
    return 'my-purchase';
  }

  if (carStatus === 'available') {
    return 'available';
  }

  if (apiCartData && apiCartData.vehicle.sku === sku) {
    return 'continue-purchase';
  }

  return 'not-available';
};

export const formatSpecs = (productDetails: ProductDetailsPayload) => ({
  items: [
    {
      key: KEY_ITEMS.KILOMETERS,
      value: productDetails.kilometres,
    },
    {
      key: KEY_ITEMS.BUILD_YEAR,
      value: `${productDetails.buildYear}`,
    },
    {
      key: KEY_ITEMS.COMPLIANCE_YEAR,
      value: `${productDetails.complianceYear}`,
    },
    {
      key: KEY_ITEMS.SEATS,
      value: `${productDetails.seats}`,
    },
    {
      key: KEY_ITEMS.KEYS,
      value: productDetails.numberOfKeys,
    },
    {
      key: KEY_ITEMS.EXTERIOR_COLOR,
      value: productDetails.colour,
    },
    {
      key: KEY_ITEMS.FUEL_TYPE,
      value: productDetails.fuelType,
    },
    {
      key: KEY_ITEMS.DRIVE_TYPE,
      value: productDetails.driveType,
    },
    {
      key: KEY_ITEMS.ENGINE_SIZE,
      value: productDetails.engineSize,
    },
    {
      key: KEY_ITEMS.ENGINE_POWER,
      value: productDetails.enginePower,
    },
    {
      key: KEY_ITEMS.VIN_NUMBER,
      value: productDetails.vin,
    },
  ],
  carSummary: {
    bodyType: productDetails.body,
    exteriorColour: productDetails.colour,
    numberOfKeys: `${productDetails.numberOfKeys}`,
    vin: productDetails.vin,
    complianceYear: `${productDetails.complianceYear}`,
    buildYear: `${productDetails.buildYear}`,
    odometer: productDetails.kilometres,
    transmission: productDetails.transmission,

    fuelType: productDetails.fuelType,
    fuelTankCapacity: productDetails.fuelCapacity,
    fuelConsumption: productDetails.fuelConsumption,
    emissions: productDetails.emissions,
    batteryCapacity: productDetails.batteryCapacity,
    electricRange: productDetails.electricRange,
    energyConsumption: productDetails.energyConsumption,

    ancapSafetyRating: productDetails.ancapRating,

    driveType: productDetails.driveType,
    cylinders: productDetails.cylinders,
    gearbox: productDetails.gears,
    enginePower: productDetails.enginePower,
    engineSize: productDetails.engineSize,
    towCapBraked: productDetails.towCapBraked,

    seats: `${productDetails.seats}`,
    weight: productDetails.weight,
    length: productDetails.length,
  },
});

export const getTargetViewType = (galleryModalState: GalleryModalState): CarGalleryNavigation => {
  if (galleryModalState.displayState === 'imperfections') {
    return 'imperfections';
  } else if (galleryModalState.displayState === 'gallery') {
    return 'gallery';
  } else if (galleryModalState.exterior && !galleryModalState.openDoors) {
    return 'exterior';
  } else if (galleryModalState.exterior && galleryModalState.openDoors) {
    return 'open';
  } else if (!galleryModalState.exterior) {
    return 'interior';
  }

  return 'exterior';
};
